import { useState } from 'react';
import {
  Upload,
  Clock,
  Target,
  Building,
  Star,
  FileText,
  Map,
  BarChart,
  DollarSign,
  Calendar,
  CheckCircle,
  Users,
  TrendingUp,
  Scale
} from 'lucide-react';
const Main = () => {
  const [taskStage, setTaskStage] = useState('input');

  const lawFirmMetrics = {
    "Global IP Partners": {
      overallScore: 92,
      metrics: {
        speed: 90,
        quality: 95,
        costEfficiency: 88,
        clientSatisfaction: 94
      },
      specialties: ["AI/ML", "Software", "Life Sciences"],
      completedTasks: 234,
      avgResponse: "4 hours",
      geography: ["UK", "EU", "US"],
      typicalTimelines: {
        "Trademark Filing": "2-3 days",
        "Portfolio Review": "1-2 weeks",
        "IP Strategy": "2-4 weeks"
      }
    }
  };

  // Mock historic performance data
  const historicPerformance = {
    averageCompletion: {
      "Trademark Filing": "2.5 days",
      "Portfolio Review": "10 days",
      "IP Strategy": "21 days"
    },
    costRanges: {
      "Trademark Filing": "£500-£1,500",
      "Portfolio Review": "£2,000-£5,000",
      "IP Strategy": "£5,000-£15,000"
    }
  };
  return (
    <div className="space-y-6">
      {/* Client Task Upload */}
      <div className='bg-white p-[25px] rounded-[5px] shadow-2xl'>
        <div>
          <div className="flex items-center gap-2">
            <Upload className="w-5 h-5" />
            <span className='font-bold'>Submit New Task</span>
          </div>
          <div>
            Upload your IP task for law firm proposals
          </div>
        </div>
        <div>
          <div className="space-y-4 mt-2">
            {/* Basic Requirements */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Task Type</label>
                <select className="w-full focus:outline-none rounded-2xl py-2 px-4 border-4 border-main text-primary">
                  <option value="">Select Task Type</option>
                  <option value="trademark">Trademark Filing</option>
                  <option value="portfolio">5 Days</option>
                  <option value="strategy">IP Strategy</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-medium">Urgency Level</label>
                <select className="w-full focus:outline-none rounded-2xl py-2 px-4 border-4 border-main text-primary">
                  <option value="">Select Urgency</option>
                  <option value="standard">Standard</option>
                  <option value="urgent">Urgent</option>
                  <option value="critical">Critical</option>
                </select>
              </div>
            </div>

            {/* Timeline and Budget */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Desired Completion</label>
                <input type="date"
                  className="w-full border-4 border-[#033d44] p-2.5 text-black bg-[#fbfbfb] rounded-2xl mb-3 focus:outline-none" placeholder="Enter Date" />
              </div>
              <div>
                <label className="text-sm font-medium">Budget Range</label>
                <select className="w-full focus:outline-none rounded-2xl py-2 px-4 border-4 border-main text-primary">
                  <option value="">Select Budget Range</option>
                  <option value="low">£500-£2,000</option>
                  <option value="medium">£2,000-£5,000</option>
                  <option value="high">£5,000+</option>
                </select>
              </div>
            </div>

            {/* Task Details */}
            <div>
              <label className="text-sm font-medium">Task Details</label>
              <textarea
                className="focus:outline-none shadow-xl mb-3 border-4 border-[#033d44] resize-none block p-2.5 w-full text-black bg-[#fbfbfb] rounded-2xl border focus:ring-mainBlue focus:border-mainBlue h-32"
                placeholder='Describe your requirements, objectives, and any specific preferences...'
              >
              </textarea>
            </div>

            {/* Preferences */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Geographic Preference</label>
                <select className="w-full focus:outline-none rounded-2xl py-2 px-4 border-4 border-main text-primary">
                  <option value="">Select Region</option>
                  <option value="uk">UK Based</option>
                  <option value="eu">EU Experience</option>
                  <option value="global">Global Coverage</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-medium">Expertise Level</label>
                <select className="w-full focus:outline-none rounded-2xl py-2 px-4 border-4 border-main text-primary">
                  <option value="">Select Expertise Level</option>
                  <option value="partner">Partner Level</option>
                  <option value="senior">Senior Associate</option>
                  <option value="mixed">Mixed Team</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* AI Recommendations */}
      <div className='bg-white p-[25px] rounded-[5px] shadow-2xl'>
        <div>
          <div className="flex items-center gap-2">
            <Star className="w-5 h-5" />
            <span className='font-bold'> Recommended Law Firms</span>
          </div>
          <div>
            Based on your requirements and historic performance
          </div>
        </div>
        <div>
          <div className="space-y-4 mt-2">
            {/* Top Matched Firm */}
            <div className="border rounded-lg p-4">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="font-medium">Global IP Partners</h3>
                  <p className="text-sm text-gray-500">234 similar tasks completed</p>
                </div>
                <div className="bg-[#d1fae5] text-[#065f46] text-xs font-medium me-2 px-2.5 py-0.5 rounded-sm">
                  92% Match
                </div>
              </div>

              {/* Performance Metrics */}
              <div className="grid grid-cols-4 gap-4 mb-4">
                <div>
                  <div className="text-sm text-gray-500 mb-1">Speed</div>
                  <div className="w-full bg-gray-200 rounded-full h-2.5">
                    <div className="bg-green h-2 rounded-full" style={{ width: `90%` }}></div>
                  </div>
                  <div className="text-sm mt-1">90/100</div>
                </div>
                <div>
                  <div className="text-sm text-gray-500 mb-1">Quality</div>
                  <div className="w-full bg-gray-200 rounded-full h-2.5">
                    <div className="bg-green h-2 rounded-full" style={{ width: `95%` }}></div>
                  </div>
                  <div className="text-sm mt-1">95/100</div>
                </div>
                <div>
                  <div className="text-sm text-gray-500 mb-1">Cost</div>
                  <div className="w-full bg-gray-200 rounded-full h-2.5">
                    <div className="bg-green h-2 rounded-full" style={{ width: `88%` }}></div>
                  </div>
                  <div className="text-sm mt-1">88/100</div>
                </div>
                <div>
                  <div className="text-sm text-gray-500 mb-1">Satisfaction</div>
                  <div className="w-full bg-gray-200 rounded-full h-2.5">
                    <div className="bg-green h-2 rounded-full" style={{ width: `94%` }}></div>
                  </div>
                  <div className="text-sm mt-1">94/100</div>
                </div>
              </div>

              {/* Timeline and Cost Prediction */}
              <div className="bg-gray-50 p-3 rounded-lg mb-4">
                <h4 className="font-medium mb-2">AI Prediction</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm text-gray-500">Expected Timeline</div>
                    <div className="font-medium">12-15 days</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">Estimated Cost</div>
                    <div className="font-medium">£3,500-£4,200</div>
                  </div>
                </div>
              </div>

              {/* Quick Stats */}
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="text-sm text-gray-500">Avg Response</div>
                  <div className="font-medium">4 hours</div>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="text-sm text-gray-500">Success Rate</div>
                  <div className="font-medium">97%</div>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="text-sm text-gray-500">Revision Rate</div>
                  <div className="font-medium">2.1%</div>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex gap-2">
                  {["UK", "EU", "US"].map((region) => (
                    <div key={region} className='text-xs font-medium me-2 px-2.5 py-0.5 rounded-sm bg-blue-100 text-blue-800'>
                      {region}
                    </div>
                  ))}
                </div>
                <button className="flex items-center gap-2 px-4 py-2 bg-green text-white rounded-lg">
                  Request Proposal
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Historic Performance */}
      <div className='bg-white p-[25px] rounded-[5px] shadow-2xl'>
        <div>
          <div className="flex items-center gap-2">
            <BarChart className="w-5 h-5" />
            <span className='font-bold'> Market Intelligence</span>
          </div>
        </div>
        <div>
          <div className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 border rounded-lg">
                <h3 className="font-medium mb-3">Average Completion Times</h3>
                <div className="space-y-2">
                  {Object.entries(historicPerformance.averageCompletion).map(([task, time]) => (
                    <div key={task} className="flex justify-between text-sm">
                      <span className="text-gray-500">{task}</span>
                      <span>{time}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="p-4 border rounded-lg">
                <h3 className="font-medium mb-3">Typical Cost Ranges</h3>
                <div className="space-y-2">
                  {Object.entries(historicPerformance.costRanges).map(([task, range]) => (
                    <div key={task} className="flex justify-between text-sm">
                      <span className="text-gray-500">{task}</span>
                      <span>{range}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Main